import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { api, fileUrl } from "@/lib/api";
import { Minus, Plus, X, Tag } from "lucide-react";
import { toast } from "sonner";

export default function Cart() {
  const { items, update, remove, total } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [code, setCode] = useState("");
  const [coupon, setCoupon] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("kk_coupon") || "null");
    } catch {
      return null;
    }
  });
  const [validating, setValidating] = useState(false);

  // Re-validate stored coupon when totals/auth state change so the displayed discount stays accurate
  useEffect(() => {
    const persist = (c) => {
      if (c) localStorage.setItem("kk_coupon", JSON.stringify(c));
      else localStorage.removeItem("kk_coupon");
    };
    if (!coupon) {
      persist(null);
      return;
    }
    if (items.length === 0) return;
    api
      .post("/coupons/validate", { code: coupon.code, subtotal: total })
      .then((r) => {
        if (r.data.valid) {
          const next = { ...r.data };
          setCoupon(next);
          persist(next);
        } else {
          setCoupon(null);
          persist(null);
        }
      })
      .catch(() => {});
    // eslint-disable-next-line
  }, [total, user?.id, items.length]);

  const apply = async () => {
    if (!code.trim()) return;
    setValidating(true);
    try {
      const { data } = await api.post("/coupons/validate", {
        code: code.trim().toUpperCase(),
        subtotal: total,
      });
      if (!data.valid) {
        toast.error(data.message || "Invalid coupon");
        setValidating(false);
        return;
      }
      setCoupon(data);
      localStorage.setItem("kk_coupon", JSON.stringify(data));
      setCode("");
      toast.success(`Coupon ${data.code} applied · -₹${data.discount.toLocaleString("en-IN")}`);
    } catch {
      toast.error("Something went wrong");
    } finally {
      setValidating(false);
    }
  };

  const removeCoupon = () => {
    setCoupon(null);
    localStorage.removeItem("kk_coupon");
  };

  if (items.length === 0) {
    return (
      <main className="max-w-3xl mx-auto px-6 py-32 text-center">
        <div className="overline">Your bag</div>
        <h1 className="font-serif text-5xl mt-4">Your bag is empty</h1>
        <p className="text-[#5c5a56] mt-4">Browse the atelier and start curating yours.</p>
        <Link to="/collection" className="btn-primary mt-10 inline-flex" data-testid="cart-shop-link">
          Explore the collection
        </Link>
      </main>
    );
  }

  const discount = coupon?.discount || 0;
  const finalTotal = Math.max(0, total - discount);
  const mrpTotal = items.reduce(
    (s, i) => s + (i.compare_at_price && i.compare_at_price > i.price ? i.compare_at_price : i.price) * i.quantity,
    0
  );
  const productSavings = Math.max(0, mrpTotal - total);

  return (
    <main className="max-w-[1200px] mx-auto px-6 lg:px-10 py-16">
      <div className="overline">Your bag</div>
      <h1 className="font-serif text-5xl mt-3 mb-12">A quiet curation</h1>
      <div className="grid lg:grid-cols-[1fr_400px] gap-12">
        <div className="space-y-6">
          {items.map((it) => (
            <div
              key={it.product_id}
              className="grid grid-cols-[120px_1fr_auto] gap-6 pb-6 border-b border-[#ebe5df]"
              data-testid={`cart-item-${it.product_id}`}
            >
              <div className="aspect-square bg-[#f3efea] overflow-hidden">
                {it.image && <img src={fileUrl(it.image)} alt="" className="w-full h-full object-cover" />}
              </div>
              <div>
                <h3 className="font-serif text-2xl text-[#1a1918]">{it.name}</h3>
                <div className="text-sm mt-1 flex items-baseline gap-2">
                  <span>₹{it.price.toLocaleString("en-IN")}</span>
                  {it.compare_at_price && it.compare_at_price > it.price && (
                    <span className="text-xs text-[#5c5a56] line-through">
                      ₹{it.compare_at_price.toLocaleString("en-IN")}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3 mt-4">
                  <button
                    className="w-8 h-8 border border-[#ebe5df] flex items-center justify-center hover:border-[#1a1918]"
                    onClick={() => update(it.product_id, it.quantity - 1)}
                    data-testid={`qty-dec-${it.product_id}`}
                  >
                    <Minus size={14} />
                  </button>
                  <span className="w-8 text-center text-sm">{it.quantity}</span>
                  <button
                    className="w-8 h-8 border border-[#ebe5df] flex items-center justify-center hover:border-[#1a1918]"
                    onClick={() => update(it.product_id, it.quantity + 1)}
                    data-testid={`qty-inc-${it.product_id}`}
                  >
                    <Plus size={14} />
                  </button>
                </div>
              </div>
              <div className="text-right">
                <button
                  onClick={() => remove(it.product_id)}
                  className="text-[#5c5a56] hover:text-[#8b3a36]"
                  data-testid={`cart-remove-${it.product_id}`}
                  aria-label="Remove"
                >
                  <X size={18} />
                </button>
                <div className="font-serif text-xl mt-6">
                  ₹{(it.price * it.quantity).toLocaleString("en-IN")}
                </div>
              </div>
            </div>
          ))}
        </div>
        <aside className="bg-[#f3efea] p-8 h-fit">
          <h3 className="font-serif text-2xl mb-6">Summary</h3>
          {productSavings > 0 && (
            <div className="flex justify-between text-sm py-2 text-[#5c5a56]">
              <span>MRP</span>
              <span className="line-through">₹{mrpTotal.toLocaleString("en-IN")}</span>
            </div>
          )}
          <div className="flex justify-between text-sm py-2">
            <span className="text-[#5c5a56]">Subtotal</span>
            <span>₹{total.toLocaleString("en-IN")}</span>
          </div>
          {productSavings > 0 && (
            <div className="flex justify-between text-sm py-2 text-[#8b3a36]" data-testid="cart-mrp-savings">
              <span>You save</span>
              <span>-₹{productSavings.toLocaleString("en-IN")}</span>
            </div>
          )}
          <div className="flex justify-between text-sm py-2 border-b border-[#ebe5df]">
            <span className="text-[#5c5a56]">Shipping</span>
            <span className="text-[#365e47]">Complimentary</span>
          </div>

          {/* Coupon */}
          <div className="py-4 border-b border-[#ebe5df]">
            {coupon ? (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Tag size={14} className="text-[#c58b76]" />
                  <div>
                    <div className="font-serif text-sm">{coupon.code} applied</div>
                    <div className="text-[11px] text-[#5c5a56]">
                      -₹{discount.toLocaleString("en-IN")}
                    </div>
                  </div>
                </div>
                <button
                  onClick={removeCoupon}
                  className="text-[#8b3a36] hover:text-[#1a1918]"
                  data-testid="remove-coupon"
                  aria-label="Remove coupon"
                >
                  <X size={16} />
                </button>
              </div>
            ) : (
              <div className="flex gap-2">
                <input
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  placeholder="Coupon code"
                  className="flex-1 px-3 py-2 border border-[#ebe5df] bg-white text-sm focus:outline-none focus:border-[#c58b76]"
                  data-testid="coupon-input"
                  onKeyDown={(e) => e.key === "Enter" && apply()}
                />
                <button
                  onClick={apply}
                  disabled={validating || !code.trim()}
                  className="btn-primary !py-2 !px-4 disabled:opacity-50"
                  data-testid="apply-coupon"
                >
                  {validating ? "…" : "Apply"}
                </button>
              </div>
            )}
          </div>

          {discount > 0 && (
            <div className="flex justify-between text-sm py-2 text-[#c58b76]">
              <span>Discount</span>
              <span>-₹{discount.toLocaleString("en-IN")}</span>
            </div>
          )}
          <div className="flex justify-between font-serif text-2xl py-4">
            <span>Total</span>
            <span>₹{finalTotal.toLocaleString("en-IN")}</span>
          </div>
          <button
            onClick={() => (user ? navigate("/checkout") : navigate("/login?redirect=/checkout"))}
            className="btn-primary w-full justify-center mt-4"
            data-testid="checkout-btn"
          >
            {user ? "Proceed to checkout" : "Sign in to checkout"}
          </button>
          <p className="text-[11px] text-[#5c5a56] mt-4 text-center tracking-wider">
            Secure checkout · Razorpay
          </p>
        </aside>
      </div>
    </main>
  );
}
